import { notification } from 'antd'
import i18n from 'i18n'
import localforage from 'myStore'
import type { ApolloClient } from '@apollo/client'

import { store as reduxStore } from 'index'
import { setEnvParam } from 'env'

import { ListenerObj } from 'pages/main/data/defConstants'

import { checkOffline } from 'popups'

import Ngql from 'gql/ninegql'
import { parseJwt, decodeJwtPayload, processError } from 'utils'
import NativeInterface from 'services/native-interface/om-native-client'
import { captureTokenRefreshFailure, captureAbnormalLogout } from 'services/sentry'

import { isBizErrorCode, BIZ_ERROR_CODES_ENABLED } from '../bizError'
import { showBizErrorModal } from '../bizError/modal'

// Type definitions
// B-5: authClient를 any 대신 로컬 인터페이스로 좁힌다. RestClient를 값으로 import하면
// restClient→jwt 순환 참조가 되므로, 사용 메서드만 추린 구조적 타입을 정의한다.
interface AuthRefreshResponse {
  code: number
  data?: {
    access_token?: string
    token_type?: string
    refresh_token?: string
  }
}
interface AuthHttpClient {
  post: (url: string, body: unknown) => Promise<{ data: AuthRefreshResponse }>
}

interface ClientInfo {
  clientForRefreshToken: ApolloClient<any> | null
  authClient: AuthHttpClient | null
}

// interface TokenAuthResponse {
//   token: string
//   refreshExpiresIn: number
//   refreshToken: string
// }

interface RefreshTokenResponse {
  token: string
  refreshExpiresIn: number
  refreshToken: string
}

interface UpdateTokenReturnType {
  retPromise: Promise<Response> | null
  ret: boolean
}

interface ExtraOptions {
  getHeaders?: (token: string) => Promise<Record<string, any>>
}

interface JwtPayload {
  exp?: number
  services?: any
  email?: string
  [key: string]: any
}

export class TokenRequestFailedError extends Error {
  public readonly code = 'TOKEN_REQUEST_FAILED' as const
  constructor(message = 'token request failed.') {
    super(message)
    this.name = 'TokenRequestFailedError'
  }
}

const clientInfo: ClientInfo = {
  clientForRefreshToken: null,
  authClient: null,
}

export function setClientForAuth(
  forRefreshToken: ApolloClient<any>,
  forAuth: AuthHttpClient,
): void {
  clientInfo.clientForRefreshToken = forRefreshToken
  clientInfo.authClient = forAuth
}

const mutationLogin = Ngql.Mutation('tokenAuth', {
  tokenAuth: Ngql.Node(
    {
      token: true,
      refreshExpiresIn: true,
      refreshToken: true,
    },
    null,
    false,
    {
      email: Ngql.Var('email', 'String!'),
      password: Ngql.Var('password', 'String!'),
      serverType: Ngql.Var('serverType', 'String'),
      serverUri: Ngql.Var('serverUri', 'String'),
    },
  ),
})

const mutationUpdataToken = Ngql.Mutation('refreshToken', {
  refreshToken: Ngql.Node(
    {
      token: true,
      refreshExpiresIn: true,
      refreshToken: true,
    },
    null,
    false,
    {},
  ),
})

interface WaitingRequest {
  resolve: (value: string) => void
  reject: (reason?: any) => void
}

interface UpdateInfo {
  isUpdating: boolean
  waitingReq: WaitingRequest[]
  updateTokenListener: any
}

// B-1: updateByCommonAuth/updateByGQL/desktop 공통 후처리 입력
interface TokenUpdateResult {
  tokenType: string
  accessToken: string
  refreshToken?: string
  // 이 인증 흐름이 refreshToken을 발급하지 않음을 명시(데스크톱 전용). undefined를 센티넬로 쓰지 않는다.
  noRefreshToken?: boolean
}

const updateInfo: UpdateInfo = {
  isUpdating: false,
  waitingReq: [],
  updateTokenListener: new ListenerObj('updateToken'),
}

export function addUpdateTokenListener(listener: (token: string) => void): void {
  updateInfo.updateTokenListener.add(listener)
}

export function removeUpdateTokenListener(listener: (token: string) => void): void {
  updateInfo.updateTokenListener.remove(listener)
}

// ─── 공통 후처리 헬퍼 (B-1) ──────────────────────────────
function resolveWaiting(token: string): void {
  updateInfo.waitingReq.forEach((promise) => promise.resolve(token))
  updateInfo.waitingReq = []
}

function rejectWaiting(ex: unknown): void {
  updateInfo.waitingReq.forEach((promise) => promise.reject(ex))
  updateInfo.waitingReq = []
}

// 양 경로(commonAuth/GQL) 공통 로그아웃 판정 — A-4 비대칭 해소
function isAuthFailure(ex: unknown): boolean {
  const cause = (ex as { cause?: { statusCode?: number } } | null)?.cause
  if (cause != null && (cause.statusCode === 401 || cause.statusCode === 402)) {
    return true
  }
  // restClient.processResponse는 RestClientError(status 필드)로 reject한다.
  // 401은 processResponse의 handleTokenRevoked가 선처리하지만, 402 및 향후
  // processResponse 변경에 대비해 status도 판정한다(독립 검증 반영).
  const status = (ex as { status?: number } | null)?.status
  if (status === 401 || status === 402) {
    return true
  }
  const message = (ex as { message?: string } | null)?.message
  return message != null && message.indexOf('Invalid or expired refresh token') > -1
}

function describeError(ex: unknown): string {
  if (ex != null && typeof ex === 'object') {
    const e = ex as { name?: string; message?: string }
    return `${e.name ?? 'Error'}: ${e.message ?? ''}`
  }
  return String(ex)
}

// 성공 후처리: 저장 + 큐 resolve + 플래그 리셋 + notify.
// 검증은 모두 setItem 이전에 수행 → 실패 시 기존 저장값을 훼손하지 않는다(비파괴, A-6).
async function applyTokenUpdate(result: TokenUpdateResult): Promise<string> {
  if (result.accessToken == null || result.accessToken.trim().length === 0) {
    throw new TokenRequestFailedError('updateToken - empty token')
  }
  // A-6: token_type null 검증
  if (result.tokenType == null || result.tokenType.trim().length === 0) {
    throw new TokenRequestFailedError('updateToken - empty tokenType')
  }
  // A-6: noRefreshToken이 아닌 한 refreshToken을 반드시 검증(undefined/null/빈값 모두 throw)
  const persistRefreshToken = result.noRefreshToken !== true
  if (
    persistRefreshToken &&
    (result.refreshToken == null || result.refreshToken.trim().length === 0)
  ) {
    throw new TokenRequestFailedError('updateToken - empty refreshToken')
  }

  // B-3: 병렬 저장. token/jmap.token은 마지막 배치(읽기측 token→tokenType 순서와 정합)
  const metaWrites: Promise<unknown>[] = [
    localforage.setItem('app.user.tokenType', result.tokenType),
    localforage.setItem('app.user.jmap.tokenType', 'Bearer'),
  ]
  if (persistRefreshToken) {
    metaWrites.push(localforage.setItem('app.user.refreshToken', result.refreshToken))
  }
  await Promise.all(metaWrites)
  await Promise.all([
    localforage.setItem('app.user.token', result.accessToken),
    localforage.setItem('app.user.jmap.token', result.accessToken),
  ])

  const parsed = decodeJwtPayload(result.accessToken) as JwtPayload | null
  if (parsed != null && parsed.exp != null) {
    await localforage.setItem('app.user.expiresIn', parsed.exp)
  }

  // SE-4: 상태 리셋을 먼저, notify는 마지막(리스너 예외가 isUpdating을 stranded 시키지 않도록)
  resolveWaiting(result.accessToken)
  updateInfo.isUpdating = false
  // 저장·큐 resolve가 끝난 시점이므로 갱신은 이미 성공이다. notify 리스너의 예외가
  // 성공한 갱신을 실패(null 반환→오로그아웃)로 뒤집지 않도록 격리한다.
  try {
    updateInfo.updateTokenListener.notify(result.accessToken)
  } catch (notifyErr) {
    console.error('[JWT] updateTokenListener.notify failed', describeError(notifyErr))
  }
  return result.accessToken
}

// 실패 후처리: 큐 reject + 플래그 리셋 + 로그아웃 판정(A-4 + 일관화)
function failTokenUpdate(ex: unknown): undefined {
  const detail = describeError(ex)
  console.error('[JWT] updateToken failed', detail)
  // D-3: 갱신 실패 관측(noisy drop되는 TokenRequestFailedError와 별도로 실패율 추적)
  captureTokenRefreshFailure(detail)
  rejectWaiting(ex)
  updateInfo.isUpdating = false
  if (isAuthFailure(ex)) {
    handleTokenRevoked(detail)
  }
  return undefined
}

async function updateByCommonAuth(refreshToken: string): Promise<string | undefined> {
  try {
    const res = await clientInfo.authClient!.post('/auth/refresh', {
      refresh_token: refreshToken,
    })
    // A-5 동등 방어: res.data / res.data.data null 보호
    if (res.data?.code !== 200 || res.data?.data == null) {
      throw new TokenRequestFailedError('updateToken - bad response')
    }
    const d = res.data.data
    // undefined는 applyTokenUpdate의 A-6 런타임 검증(빈값 throw)이 걸러낸다 — as 캐스트로 은폐하지 않음
    return await applyTokenUpdate({
      tokenType: d.token_type ?? '',
      accessToken: d.access_token ?? '',
      refreshToken: d.refresh_token,
    })
  } catch (ex) {
    return failTokenUpdate(ex)
  }
}

async function updateByGQL(): Promise<string | undefined> {
  try {
    const res = await Ngql.GQLObj(mutationUpdataToken as any, {}).mutate(
      {},
      clientInfo.clientForRefreshToken!,
    )
    // A-5: res.data / refreshToken null 방어
    const rt = res.data?.refreshToken as RefreshTokenResponse | undefined
    if (rt?.token == null || rt.token.trim().length === 0) {
      throw new TokenRequestFailedError('updateToken - empty token')
    }
    return await applyTokenUpdate({
      tokenType: 'JWT',
      accessToken: rt.token,
      refreshToken: rt.refreshToken,
    })
  } catch (ex) {
    return failTokenUpdate(ex)
  }
}

// 데스크톱 세션(refreshToken 미발급, NativeInterface 갱신) — 누락-2: notify + 동시성 보호
async function updateByDesktopSession(): Promise<string | null> {
  const isDesktopSession = await localforage.getItem<boolean>('app.user.isDesktopSession')
  if (isDesktopSession !== true) {
    return null
  }
  if (updateInfo.isUpdating) {
    return new Promise<string>((resolve, reject) => {
      updateInfo.waitingReq.push({ resolve, reject })
    })
  }
  updateInfo.isUpdating = true
  try {
    const accessToken = await NativeInterface.refreshToken()
    if (accessToken == null || accessToken.length === 0) {
      throw new TokenRequestFailedError('desktopSession accessToken empty')
    }
    // noRefreshToken: 데스크톱은 refreshToken을 발급받지 않음. applyTokenUpdate 재사용으로
    // jmap.token/jmap.tokenType/expiresIn 저장 + 상태 리셋 + notify를 일괄 처리(SE-1).
    return await applyTokenUpdate({ tokenType: 'JWT', accessToken, noRefreshToken: true })
  } catch (ex) {
    return failTokenUpdate(ex) ?? null
  }
}

export async function updateToken(): Promise<string | null> {
  const refreshToken = await localforage.getItem<string>('app.user.refreshToken')
  if (refreshToken == null) {
    return updateByDesktopSession()
  }
  // 이미 갱신 중이면 대기 큐로
  if (updateInfo.isUpdating) {
    return new Promise<string>((resolve, reject) => {
      updateInfo.waitingReq.push({ resolve, reject })
    })
  }
  updateInfo.isUpdating = true // 동기 선점 — 두번째 진입은 위 큐로

  // GAP-1: isUpdating 선점 후 updateByX 진입 전(getItem/decode)에서 throw하면 isUpdating이
  // stranded true로 남아 큐가 영구 정지된다. try/catch로 반드시 리셋한다.
  // (updateByCommonAuth/GQL은 자체 try/catch로 리셋하므로 이 catch는 getItem/decode 예외용)
  try {
    const token = await localforage.getItem<string>('app.user.token')
    const parsedCommonToken = decodeJwtPayload(token || '') as JwtPayload | null
    const ret =
      parsedCommonToken != null && parsedCommonToken.services != null
        ? await updateByCommonAuth(refreshToken)
        : await updateByGQL()
    return ret || null
  } catch (ex) {
    return failTokenUpdate(ex) ?? null
  }
}

// access token의 만료 여부를 판단한다. app.user.expiresIn은 JWT exp(초 단위)를 그대로 저장한 값이다.
export async function isAccessTokenExpired(): Promise<boolean> {
  const expiresIn = await localforage.getItem<number>('app.user.expiresIn')
  if (expiresIn == null || expiresIn === 0) {
    return false
  }
  return Date.now() > expiresIn * 1000
}

// 만료된 경우에만 updateToken을 호출한다. 유효 토큰 상태에선 네트워크 호출이 발생하지 않는다.
export async function updateTokenIfExpired(): Promise<string | null> {
  if (!(await isAccessTokenExpired())) {
    return null
  }
  return updateToken()
}

// 소문자로 전환하여 header 중복 제거.
function removeDuplicateHeaders(
  target: Record<string, string>,
  headers?: HeadersInit,
): Record<string, string> {
  if (headers != null) {
    if (headers instanceof Headers) {
      headers.forEach((value, key) => {
        target[key.toLowerCase()] = value
      })
    } else if (Array.isArray(headers)) {
      headers.forEach(([key, value]) => {
        target[key.toLowerCase()] = value
      })
    } else {
      Object.keys(headers).forEach((key) => {
        target[key.toLowerCase()] = (headers as Record<string, string>)[key]
      })
    }
  }
  return target
}

export function callUpdateToken(
  uri: string | Request | URL,
  options: RequestInit,
  extraOptions: ExtraOptions = {},
): UpdateTokenReturnType {
  // A-3: updateToken()은 async라 항상 Promise를 반환하므로 죽은 분기(if updateHandle != null)를 제거.
  // retPromise는 항상 생성되어 호출부의 retPromise! 단언은 그대로 안전하다.
  const retPromise = new Promise<Response>((resolve, reject) => {
    updateToken()
      .then(async (token) => {
        if (token != null) {
          options.headers = removeDuplicateHeaders({}, options.headers)
          if (extraOptions != null && extraOptions.getHeaders != null) {
            const headers = await extraOptions.getHeaders(token)
            if (headers != null) {
              removeDuplicateHeaders(options.headers as Record<string, string>, headers)
            }
          } else {
            ;(options.headers as Record<string, string>).authorization = `JWT ${token}`
          }
          const retryOptions = { ...options }
          const { timeoutId: retryTimeoutId } = createFetchAbortController(
            retryOptions,
            FETCH_TIMEOUT_MS,
          )
          fetch(uri, retryOptions)
            .then((res2) => {
              resolve(res2)
            })
            .catch((err) => {
              reject(err)
            })
            .finally(() => clearTimeout(retryTimeoutId))
        } else {
          // 갱신 실패(null): 재시도 없이 종료. 로그아웃 판정은 failTokenUpdate(isAuthFailure)에
          // 중앙화되어 있으므로 여기서 무조건 로그아웃하지 않는다 — 일시 네트워크 오류로 인한
          // 갱신 실패까지 강제 로그아웃되는 과잉 처리를 방지(독립 리뷰 반영).
          reject(new TokenRequestFailedError())
        }
      })
      .catch((err) => {
        reject(err)
      })
  })

  return {
    retPromise,
    ret: true,
  }
}

export async function customFetchWithToken(
  uri: string | Request | URL,
  options: RequestInit,
): Promise<Response> {
  let isTokenEmpty: boolean | null = null
  const token = await localforage.getItem<string>('app.user.token')

  if (token == null || token.trim().length === 0) {
    isTokenEmpty = true
  }

  return customFetch(uri, options, isTokenEmpty)
}

// [fix] fetch 요청에 타임아웃이 없어 네트워크 hang 시 Promise가 영원히 settle되지 않는 문제 방지.
// AbortController로 60초 후 요청을 강제 중단한다.
const FETCH_TIMEOUT_MS = 60_000

function createFetchAbortController(
  options: RequestInit,
  timeoutMs: number = FETCH_TIMEOUT_MS,
): { controller: AbortController; timeoutId: ReturnType<typeof setTimeout> } {
  const controller = new AbortController()
  if (options.signal) {
    options.signal.addEventListener('abort', () => controller.abort(), { once: true })
  }
  options.signal = controller.signal
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  return { controller, timeoutId }
}

// 토큰 갱신 전용 fetch. 토큰 재발급/재시도 로직 없이 AbortController 타임아웃만 적용한다.
// refreshToken HttpLink가 이 함수를 쓰지 않고 기본 fetch를 쓰면, GQL 토큰 갱신 요청이
// 네트워크 hang 시 영원히 settle되지 않아 updateInfo.waitingReq 큐에 쌓인 모든 인증 요청이
// 무한 대기에 빠진다. customFetch를 재사용하면 갱신 요청이 또 갱신을 트리거하는 순환이
// 생기므로, 순수 타임아웃 fetch를 별도로 둔다.
export function fetchWithTimeout(
  uri: string | Request | URL,
  options: RequestInit = {},
  timeoutMs: number = FETCH_TIMEOUT_MS,
): Promise<Response> {
  const { timeoutId } = createFetchAbortController(options, timeoutMs)
  return fetch(uri, options).finally(() => clearTimeout(timeoutId))
}

export function customFetch(
  uri: string | Request | URL,
  options: RequestInit,
  isTokenEmpty: boolean | null,
): Promise<Response> {
  if (isTokenEmpty === true) {
    const callRet = callUpdateToken(uri, options, {})
    return callRet.retPromise!
  }

  const { timeoutId } = createFetchAbortController(options)
  const initialRequest = fetch(uri, options).finally(() => clearTimeout(timeoutId))

  let responseData: Response | null = null
  return initialRequest
    .then((response) => {
      responseData = response
      if (isIpNotAllowedResponse(response)) {
        handleIpNotAllowed()
        return Promise.reject(new Error('ip-not-allowed'))
      }
      if (
        options.method &&
        options.method.toLowerCase() !== 'head' &&
        response.status !== 204 &&
        response.status !== 304
      ) {
        const ret = response.clone().json()
        return ret
      }
      return Promise.resolve(null)
    })
    .then((jsonBody: any) => {
      // biz 플랜 계약/권한 상실(HTTP 494 + top-level body code 710/711/712/713/494) — 강제 로그아웃. (OMD-27)
      // (494 body code 는 711 과 동일 문구로 처리) 기존 토큰 갱신/에러 분기(아래)보다 먼저 처리하고 즉시 종료해,
      // 494 가 불필요한 갱신 경로로 새지 않게 한다. 환경 게이트로 감싸 운영/스테이징 빌드에서는 정적 제거된다.
      if (
        BIZ_ERROR_CODES_ENABLED &&
        responseData!.status === 494 &&
        jsonBody &&
        isBizErrorCode(jsonBody.code)
      ) {
        handleBizAccessBlocked(jsonBody.code)
        return responseData!
      }
      if (
        (responseData!.status >= 400 && jsonBody && jsonBody.code !== 200) ||
        (responseData!.status === 200 && jsonBody && jsonBody.code === 401)
      ) {
        const error = jsonBody
        let ret: UpdateTokenReturnType = { retPromise: null, ret: false }

        // A-1: message?.includes 로 null-safety. A-2: 단일 지점에서만 callUpdateToken.
        const isExpired = (code?: number | string, message?: string): boolean =>
          code === 401 ||
          (message?.includes('Signature has expired') ?? false) ||
          (message?.includes('jwt expired') ?? false)

        // G-1c: 만료/인증실패 판정을 processError 콜백 '안'에서 하고 첫 매칭 시 순회 중단
        // (콜백이 무조건 false면 graphQLErrors 배열에서 마지막 에러만 캡처돼 만료를 놓침)
        let message: string | undefined
        let matchedExpired = false
        let matchedNotAuth = false
        processError(error, (c: number | string, m: string | undefined) => {
          message = m
          if (isExpired(c, m)) {
            matchedExpired = true
            return true
          }
          if (c === 500 || m === 'Not authenticated') {
            matchedNotAuth = true
            return true
          }
          return false
        })
        // G-1: processError가 콜백을 호출하지 않는 {success:false, message:'jwt expired'}(code 없음) 보강
        if (
          !matchedExpired &&
          !matchedNotAuth &&
          error.success === false &&
          isExpired(undefined, error.message)
        ) {
          matchedExpired = true
          message = error.message
        }

        if (matchedExpired) {
          // A-3: callUpdateToken은 항상 retPromise를 생성하므로 null 분기/알림 불필요
          ret = callUpdateToken(uri, options, {})
        } else if (matchedNotAuth) {
          // G-1b: 로그아웃 분기 보존 + 일관화(직접 dispatch → handleTokenRevoked)
          handleTokenRevoked('not authenticated')
          throw new Error(message)
        }

        if (ret.retPromise != null) {
          return ret.retPromise
        }
      }

      return responseData!
    })
    .catch((err) => {
      // offline UI 표시(autoReload)는 부수효과로 유지, 디버그 로깅은 제거
      void checkOffline({ autoReload: true })
      throw err
    })
}

export function customFetchForXML(
  uri: string | Request | URL,
  options: RequestInit,
  extraOptions?: ExtraOptions,
): Promise<Response> {
  const { timeoutId } = createFetchAbortController(options)
  const initialRequest = fetch(uri, options).finally(() => clearTimeout(timeoutId))

  let responseData: Response | null = null
  return initialRequest
    .then((response) => {
      responseData = response
      if (isIpNotAllowedResponse(response)) {
        handleIpNotAllowed()
        return Promise.reject(new Error('ip-not-allowed'))
      }
      if (response.status === 401) {
        const ret = callUpdateToken(uri, options, extraOptions || {})
        return ret.retPromise!
      }

      return responseData
    })
    .catch((err) => {
      // offline UI 표시(autoReload)는 부수효과로 유지, 디버그 로깅은 제거
      void checkOffline({ autoReload: true })
      throw err
    })
}

export async function login(email: string, password: string): Promise<boolean | undefined> {
  return Ngql.GQLObj(mutationLogin as any, {
    vars: {
      email,
      password,
      serverType: 'exchange',
      serverUri: 'mail.ninefolders.xyz',
    },
    useUI: false,
  })
    .mutate()
    .then(async (res: any) => {
      await localforage.setItem('app.user.token', res.data.tokenAuth.token)
      await localforage.setItem('app.user.refreshExpiresIn', res.data.tokenAuth.refreshExpiresIn)
      await localforage.setItem('app.user.refreshToken', res.data.tokenAuth.refreshToken)
      // 누락-7: 선제 갱신(isAccessTokenExpired)이 첫 갱신 전에도 동작하도록 expiresIn 저장
      const parsed = decodeJwtPayload(res.data.tokenAuth.token) as JwtPayload | null
      if (parsed?.exp != null) {
        await localforage.setItem('app.user.expiresIn', parsed.exp)
      }

      return true
    })
    .catch((error: any) => {
      notification.warning({
        message: error.code,
        description: error.message,
      })
      return undefined
    })
}

const queryUserInfo = (Ngql.Query as any)(
  'userInfo',
  {
    systemInfo: (Ngql.Node as any)({
      appName: true,
      description: true,
      adminEmail: true,
      version: true,
      locale: true,
      timezone: true,
    }),
    serverType: true,
  },
  null,
  false,
)

export async function register(): Promise<boolean> {
  function fakeRegister(): Promise<boolean> {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(true)
      }, 200)
    })
  }

  return fakeRegister()
}

interface UserInfo {
  name: string
  uid: string
  email: string
  photoURL?: string
  role: string
  lastLogin: string
  serverType: string
}

export async function currentAccount(): Promise<UserInfo | false> {
  const token = await localforage.getItem<string>('app.user.token')

  if (token && token.length > 0) {
    return Ngql.GQLObj(queryUserInfo as any, {
      useUI: false,
    })
      .query()
      .then((res: any) => {
        setEnvParam('serverType', res.data.serverType)
        reduxStore.dispatch({
          type: 'menu/RELOAD',
        })

        if (res.data.systemInfo == null) {
          const tokenInfo = parseJwt(token) as JwtPayload
          return {
            name: '',
            uid: '',
            email: tokenInfo.email || '',
            photoURL: '',
            role: 'admin',
            lastLogin: '',
            serverType: res.data.serverType,
          }
        }
        return {
          name: 'Administrator',
          uid: '',
          email: res.data.systemInfo.adminEmail,
          role: 'admin',
          lastLogin: '',
          serverType: res.data.serverType,
        }
      })
      .catch((error: any) => {
        if (error) {
          if (error.code !== 401) {
            notification.warning({
              message: error.code,
              description: error.message,
            })
          }
        }
        return false
      })
  }

  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(false)
    }, 200)
  })
}

// 서버가 토큰 revoke / 세션 종료를 응답 메시지로 전달했을 때 호출.
// 동시 다발 호출(예: /calendar 진입 시 다수 API 동시 fail)을 5초 debounce 로 한 번만 처리.
// 별도 reset 신호 없이 시간 기반 비교로 자연 만료 — 마지막 호출 후 5초 경과 시 다음 호출이 통과.
// SPA에서 LOGOUT → 라우터 전환 → 재로그인까지 통상 수 초 이상 걸리므로 5초 자연 만료로 충분.
let tokenRevokedHandledAt = 0
const TOKEN_REVOKED_DEBOUNCE_MS = 5_000

// '세션 만료'는 인증된 세션 사용 중 토큰이 무효화된 경우에만 의미가 있다.
// 정상 로그아웃(토큰 의도적 제거)·이미 로그인 화면(/auth/*)·비인가 상태에서는
// 진행 중이던 요청의 갱신 실패가 handleTokenRevoked를 호출하더라도 세션 만료가 아니므로
// 알림/재로그아웃을 생략한다(정상 로그아웃 후 '세션이 만료되었습니다' 오표시 방지).
function isSessionAlreadyEnded(): boolean {
  try {
    const path = window.location?.pathname ?? ''
    if (path.indexOf('/auth/') === 0) {
      return true
    }
    const state = reduxStore.getState() as { user?: { authorized?: boolean } } | undefined
    if (state?.user?.authorized === false) {
      return true
    }
  } catch {
    // location/store 접근 불가 시 기존 동작 유지(보고)
  }
  return false
}

export function handleTokenRevoked(reason?: string): void {
  // dev 전용 EmailView 확인 페이지(/dev/mailview, OFM-233 Phase 8)는 로그인 세션과 무관하게 .eml 을
  // 실제 파이프라인으로 렌더·검토하기 위한 도구다. 이 페이지에서도 풀 앱 부트스트랩(getMyInfo/calendar/
  // carddav 등)이 백엔드 인증 실패로 auto-logout 을 유발하면 렌더 직후 언마운트돼 검토가 불가능하다.
  // 프로덕션 빌드에는 이 라우트가 존재하지 않으므로(isDevelop 게이팅) 경로 가드는 프로덕션 동작에 무영향.
  if (typeof window !== 'undefined') {
    const { pathname, hash } = window.location
    if (pathname === '/dev/mailview' || hash.includes('/dev/mailview')) {
      return
    }
  }

  // 정상 로그아웃/로그인 화면/비인가 상태면 세션 만료가 아니므로 알림·재로그아웃 생략
  if (isSessionAlreadyEnded()) {
    return
  }

  const now = Date.now()
  if (now - tokenRevokedHandledAt < TOKEN_REVOKED_DEBOUNCE_MS) {
    return
  }
  tokenRevokedHandledAt = now

  // 비정상 로그아웃 관측: 개발은 console(아래), 운영은 Sentry(isLocal에선 no-op).
  // debounce 통과 후 지점이므로 세션당 1회만 기록된다.
  console.warn('[JWT] Token revoked — auto logout', reason)
  captureAbnormalLogout('token-revoked', reason)

  notification.warning({
    // 만료 세션으로 앱 진입 시 i18n 초기화 전에 호출될 수 있어 defaultValue로 raw 키 노출 방지
    message: i18n.t('common.warning.sessionExpired', { defaultValue: '세션이 만료되었습니다' }),
    description: i18n.t('common.warning.sessionExpiredDesc', {
      defaultValue: '다시 로그인해 주세요.',
    }),
    duration: 4,
  })

  reduxStore.dispatch({ type: 'user/LOGOUT' })
}

// biz 플랜 계약 만료/해지/접근권한 상실(HTTP 494 + body code 710/711/712) 시 강제 로그아웃. (OMD-27)
// REST/GraphQL(향후 JMAP 등) 여러 경로에서 동시 다발로 호출될 수 있어, handleTokenRevoked 와는
// 별개의 단일 모듈 레벨 타임스탬프로 5초 debounce — 안내 문구/LOGOUT 을 한 번만 수행한다.
// 환경 게이트(BIZ_ERROR_CODES_ENABLED)로 감싸 운영/스테이징 빌드에서는 본 분기가 정적 제거된다.
let bizAccessBlockedHandledAt = 0
const BIZ_ACCESS_BLOCKED_DEBOUNCE_MS = 5_000

export function handleBizAccessBlocked(code: number): void {
  if (!BIZ_ERROR_CODES_ENABLED || !isBizErrorCode(code)) {
    return
  }

  const now = Date.now()
  if (now - bizAccessBlockedHandledAt < BIZ_ACCESS_BLOCKED_DEBOUNCE_MS) {
    return
  }
  bizAccessBlockedHandledAt = now

  console.warn('[JWT] biz access blocked — auto logout', code)

  // 채널1(SSO 로그인 실패)과 동일한 안내 모달(title '로그인 실패' + 코드별 문구)로 표시한 뒤 강제 로그아웃.
  showBizErrorModal(code)

  reduxStore.dispatch({ type: 'user/LOGOUT' })
}

// REST 경로 서버 요청 한도 초과(HTTP 429) 응답 시 사용자에게 안내.
// (참고: OFFICEMAIL-WEB-ACCESS-JR은 JMAP jam client 경로라 본 핸들러로는 미커버 — 7차 이월.
//  본 핸들러는 restClient 경유 429 일반 대응이다.)
// 짧은 시간 다발 발생할 수 있어 handleTokenRevoked 와 동일한 5초 debounce 로 toast 폭주를 막는다.
// 사용자가 throttle 을 인지하여 무분별한 반복 재시도(→ 서버 부하 가중)를 줄이는 것이 목적이며,
// LOGOUT 등 상태 변경은 하지 않는다(일시적 제한이므로 동작 불변).
let rateLimitedHandledAt = 0
const RATE_LIMITED_DEBOUNCE_MS = 5_000

export function handleRateLimited(serverMessage?: string): void {
  const now = Date.now()
  if (now - rateLimitedHandledAt < RATE_LIMITED_DEBOUNCE_MS) {
    return
  }
  rateLimitedHandledAt = now

  console.warn('[REST] rate limited (429)', serverMessage)

  notification.warning({
    message: i18n.t('common.warning.rateLimited', { defaultValue: '요청이 많습니다' }),
    // 서버 throttle 메시지("요청 한도를 초과했습니다. N초 후에...")가 재시도 시각을 포함하므로 우선 노출.
    description:
      serverMessage && serverMessage.length > 0
        ? serverMessage
        : i18n.t('common.warning.rateLimitedDesc', { defaultValue: '잠시 후 다시 시도해 주세요.' }),
    duration: 4,
  })
}

// IP allowlist 보안 정책 차단 응답 판별.
// 서버가 JMAP/DAV/REST 접근을 거부할 때 403 + X-OfficeMail-Auth-Error: ip-not-allowed 로 응답.
export function isIpNotAllowedResponse(response: Response): boolean {
  return (
    response.status === 403 && response.headers.get('X-OfficeMail-Auth-Error') === 'ip-not-allowed'
  )
}

// IP allowlist 차단 플래그.
// LOGOUT 시 menu/RELOAD 로 동적 라우트(myRoutes)가 잠시 비는 동안, 현재 경로가 어떤 라우트에도
// 매칭되지 않아 catch-all(<Route path="*">)이 /auth/404 로 Navigate 하는 레이스가 발생한다.
// 이 플래그가 설정돼 있으면 404 페이지가 무조건 /auth/access-denied 로 우선 리다이렉트하여,
// 403(ip-not-allowed) 상황에서는 항상 Access Denied 페이지로 귀결되도록 한다.
// 플래그는 Access Denied 페이지가 실제로 mount 될 때(clearIpNotAllowedFlag) 해제된다.
const IP_NOT_ALLOWED_FLAG_KEY = 'app.auth.ipNotAllowed'

export function markIpNotAllowed(): void {
  try {
    sessionStorage.setItem(IP_NOT_ALLOWED_FLAG_KEY, '1')
  } catch {
    // sessionStorage 접근 불가 환경은 무시
  }
}

export function isIpNotAllowedPending(): boolean {
  try {
    return sessionStorage.getItem(IP_NOT_ALLOWED_FLAG_KEY) === '1'
  } catch {
    return false
  }
}

export function clearIpNotAllowedFlag(): void {
  try {
    sessionStorage.removeItem(IP_NOT_ALLOWED_FLAG_KEY)
  } catch {
    // sessionStorage 접근 불가 환경은 무시
  }
}

// IP allowlist 차단 전역 처리. handleTokenRevoked 와 동일하게 동시 다발 호출을 debounce 로 한 번만 처리.
// 세션을 종료하고 Access Denied 페이지(/auth/access-denied)로 이동시킨다.
let ipNotAllowedHandledAt = 0
const IP_NOT_ALLOWED_DEBOUNCE_MS = 5_000

export function handleIpNotAllowed(): void {
  const now = Date.now()
  if (now - ipNotAllowedHandledAt < IP_NOT_ALLOWED_DEBOUNCE_MS) {
    return
  }
  ipNotAllowedHandledAt = now

  // 비정상 로그아웃 관측(IP allowlist 차단): 개발은 console, 운영은 Sentry. debounce 후 1회.
  console.warn('[JWT] IP not allowed — access denied')
  captureAbnormalLogout('ip-not-allowed')

  // 404 레이스 방지: LOGOUT dispatch 전에 플래그를 먼저 설정한다.
  markIpNotAllowed()
  reduxStore.dispatch({ type: 'user/LOGOUT', payload: { target: '/auth/access-denied' } })
}

export async function logout(): Promise<boolean> {
  async function fakeLogout(): Promise<boolean> {
    await localforage.removeItem('app.user.token')
    await localforage.removeItem('app.user.expiresIn')
    await localforage.removeItem('app.user.refreshToken')
    await localforage.removeItem('app.user.workspace_id')
    await localforage.removeItem('app.user.refreshExpiresIn')
    await localforage.removeItem('app.user.email')

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve(true)
      }, 200)
    })
  }

  return await fakeLogout()
}

const jwt = {
  customFetch,
  login,
  logout,
}

export default jwt
